import {
    IBookingCheckInOptions,
    ICartItemData,
    ICartItemsByFare,
    ICartItemsByGroup,
    ICartItemsByProduct,
    ICountryData,
    ICustomerDetail,
    IDropOffLocationOption,
    IPickUpLocationOption,
    IPickUpLocationOptionWithRef,
    IPickupTimeOption,
    ISetCustomerData,
} from "@interfaces";
import { breakpoint, sessionHelper, theme } from "@utilities";
import styled, { keyframes } from "styled-components";
import { CheckoutItemsActions } from "./CheckoutActions";
import { useSharedByEmail } from "../checkoutSharedHelper";
import { useCheckout } from "../CheckoutContext";
import {
    hasOnlySignupDetails,
    hasPlaceholderProfile,
    isPlaceholderName,
    isPlaceholderPhone,
    isPlaceholderPostCode,
} from "../signupOfferConfig";
import {
    resolveTravellerDetails,
    travellerNeedsDetailReview,
    travellerToReviewPrefill,
} from "../travellerDetails";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { KeyboardArrowDown, ContentCopy } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import _ from "lodash";
import moment from "moment";
import {
    checkBookingNeedsPickUpTimeID,
    formatBookingDatesByProductClass,
    getBookingCheckInOptions,
    getCartItemFareData,
    getCheckInDateItem,
    getNumPax,
    groupCartItemsByProduct,
    transformCartItemsForQuote,
    validateCheckoutStep,
} from "../checkoutHelpers";
import { ItemsByProductHeader } from "./ItemByProductHeader";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { SuccessToast } from "src/Components/toasts/SuccessToast";
import { accountApi } from "src/Api/accountApi";
import { pointsApi } from "src/Api/pointsApi";
import {
    focusInvalidTravellerField,
    travellerItemsAttrValue,
    TRAVELLER_ITEMS_ATTR,
    TRAVELLER_INDEX_ATTR,
    TRAVELLER_INVALID_CLASS,
} from "../travellerFieldFocus";
import { OptionalDataField } from "./OptionalDataField";
import {
    getMetaData,
    initializeAccountDetail,
    selectAccount,
    useAppDispatch,
    useAppSelector,
    useIsLoggedIn,
} from "@redux";
import { externalApi } from "@api";
import { useNavigate, useLocation } from "react-router-dom";
import { Suggestion, TravellerSuggestionsBox } from "./TravellerSugesstionBox";
import { TravellerRemoveBox } from "./TravellerRemoveBox";
import { BorderButton } from "src/Module/TourCheckAvail/BookNowButton";
import { PaymentOptionComponent } from "./CheckoutStep3";
import { WarnToast } from "src/Components/toasts/WarnToast";
import { guestToSetCustomerData, isAddressBookSuggestionId } from "src/Utilities/addressBook";
import { Dropdown } from "src/Module/CustomDropdown";
import { PATH_BOOK_NOW, PATH_LINK_AGENT_ACCOUNT, PATH_QUOTES } from "@routes";
import { createNewQuoteModal, addToExistingQuoteModal } from "src/Module/Modals";
import {
    createNewQuoteAndReload,
    addItemToExistingQuoteAndReload,
    loadQuotes,
} from "src/Redux/Slices/allQuotes";
import { Formik, FormikHelpers } from "formik";
import { createNewCartCustomerSchema } from "@validations";
import { BOOKING_DATE_FORMAT } from "@constants";
import { ClipLoaderCommon, InputCommon, SelectCommon, StyledError } from "@components";
import { SeparateDatePicker } from "src/Components/Common/SeparateDatePicker";
import { TextField } from "@mui/material";
import { toCountryOptions } from "src/Utilities/countryOrder";
interface ICheckoutTravelerComponentProps {
    quantityIndex: number;
    suggestionRedeemers: ICustomerDetail[];
    items: ICartItemData[];
    checkInOptionsMap: { [key: string]: IBookingCheckInOptions };
    isLoadingBookingDetails: boolean;
    onItemsChange: (items: ICartItemData[]) => void;
    onCreateNewCustomer: (customer: ISetCustomerData) => Promise<ICustomerDetail>;
    firstTravellerData?: ICustomerDetail;
    firstTravellerOptionalData?: Record<number, string>;
    globalFirstTravellerData?: ICustomerDetail;
}

const TitleModel = [
    { id: "Mr", label: "Mr" },
    { id: "Mrs", label: "Mrs" },
    { id: "Miss", label: "Miss" },
    { id: "Ms", label: "Ms" },
    { id: "Mx", label: "Mx" },
    { id: "Master", label: "Master" },
];

const calculateAge = (dob: Date | null | undefined): number | null => {
    if (!dob) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
    }
    return age >= 0 ? age : null;
};

interface IInlineTravellerFormProps {
    countriesData: ICountryData[];
    onSave: (values: ISetCustomerData, earnPoints?: boolean) => Promise<void>;
    isSubmitting: boolean;
    firstTravellerData?: ICustomerDetail;
    globalFirstTravellerData?: ICustomerDetail;
    submitRef?: React.MutableRefObject<(() => void) | null>;
    hideButton?: boolean;
    noBorder?: boolean;
    /** Offer "…and Earn Points" alongside a plain save (first traveller only). */
    showEarnPointsActions?: boolean;
    /**
     * Seeds the empty form from the signed-in account, so someone who just signed
     * up does not retype the name and email they gave a moment ago. Only fields
     * the account actually holds are filled; the rest stay blank.
     */
    accountPrefill?: Partial<ISetCustomerData>;
}

const CopyFromAboveButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <Tooltip title="Copy from above" arrow placement="top">
        <IconButton
            size="small"
            onClick={(e) => {
                e.preventDefault();
                onClick();
            }}
            sx={{
                padding: "2px",
                color: theme.colors.gray_1,
                "&:hover": { color: theme.colors.main },
            }}
        >
            <ContentCopy sx={{ fontSize: 16 }} />
        </IconButton>
    </Tooltip>
);

const InlineTravellerForm: React.FC<IInlineTravellerFormProps> = ({
    countriesData,
    onSave,
    isSubmitting,
    showEarnPointsActions,
    accountPrefill,
    firstTravellerData,
    globalFirstTravellerData,
    submitRef,
    hideButton,
    noBorder,
}) => {
    const countriesModel = toCountryOptions(countriesData);

    const initialValues: ISetCustomerData = {
        title: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
        postalCode: "",
        countryCode: "",
        dateOfBirth: "",
        customerIndex: 0,
        ...accountPrefill,
    };

    // Which save button was pressed. A ref, not state: Formik's submit runs in the
    // same tick as the click, before a state update would be visible.
    const earnPointsRef = useRef(true);

    const handleFormSubmit = async (
        values: ISetCustomerData,
        { setSubmitting, validateForm, setTouched }: FormikHelpers<ISetCustomerData>,
    ) => {
        try {
            const validationResult = await validateForm();
            if (!_.isEmpty(validationResult)) {
                setTouched({
                    title: true,
                    firstName: true,
                    lastName: true,
                    dateOfBirth: true,
                    phoneNumber: true,
                    email: true,
                    postalCode: true,
                    countryCode: true,
                });
                return;
            }
            setSubmitting(true);
            const dob = moment(values.dateOfBirth);
            const age = moment().diff(dob, "years");
            const formattedValues = {
                ...values,
                dateOfBirth: dob.format(BOOKING_DATE_FORMAT),
                age,
            };
            await onSave(formattedValues, earnPointsRef.current);
        } catch (error) {
            console.error("Submission error:", error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={createNewCartCustomerSchema}
            onSubmit={handleFormSubmit}
        >
            {({
                values,
                handleChange,
                handleBlur,
                handleSubmit,
                setFieldValue,
                setFieldTouched,
                errors,
                touched,
            }) => {
                const raw = values.phoneNumber ?? "";
                const digitCount = (raw.match(/\d/g) || []).length;
                const isTooShort = digitCount > 0 && digitCount < 4;
                const dobDate = values.dateOfBirth ? moment(values.dateOfBirth).toDate() : null;
                const age = calculateAge(dobDate);

                const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.ctrlKey || e.metaKey) return;
                    if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key))
                        return;
                    if (!/[\d()+\-\s]/.test(e.key)) e.preventDefault();
                };
                const sanitize = (str: string) => str.replace(/[^0-9()+\-\s]/g, "");
                const handlePhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
                    window.requestAnimationFrame(() => {
                        const pasted = (e.target as HTMLInputElement).value;
                        handleChange(sanitize(pasted));
                    });
                };

                const ftd = firstTravellerData || globalFirstTravellerData;
                const ftDetails = ftd ? (ftd.is_primary ? ftd.customer : ftd.guest) : undefined;
                const ftEmail = ftDetails?.email ?? ftd?.email;
                const ftPhone = String(ftDetails?.phone_number ?? ftd?.phone_number ?? "");
                const ftPostCode = ftDetails?.post_code ?? ftd?.postal_code;
                const ftCountryCode = ftDetails?.country_code ?? ftd?.country_code;

                if (submitRef) {
                    submitRef.current = handleSubmit as unknown as () => void;
                }

                return (
                    <InlineFormContainer onSubmit={handleSubmit} $noBorder={noBorder}>
                        <InlineFormRow>
                            <div className="field-title">
                                <SelectCommon
                                    name="title"
                                    handleChange={(value) => setFieldValue("title", value)}
                                    handleBlur={handleBlur}
                                    value={values.title}
                                    label="Title"
                                    required
                                    listValueMembers={TitleModel}
                                />
                            </div>
                            <div className="field-name">
                                <InputCommon
                                    name="firstName"
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    value={values.firstName}
                                    label="First name *"
                                    type="text"
                                    required
                                />
                            </div>
                            <div className="field-name">
                                <InputCommon
                                    name="lastName"
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    value={values.lastName}
                                    label="Last name *"
                                    type="text"
                                    required
                                />
                            </div>
                            <div className="field-dob">
                                <div className="dobWrapper">
                                    <SeparateDatePicker
                                        value={dobDate}
                                        onChange={(date: Date | null) => {
                                            setFieldValue(
                                                "dateOfBirth",
                                                date ? moment(date).format("YYYY-MM-DD") : "",
                                            );
                                            setFieldTouched("dateOfBirth", true);
                                        }}
                                        label="Date of birth *"
                                        name="dateOfBirth"
                                        handleBlur={() => setFieldTouched("dateOfBirth", true)}
                                        maxDate={
                                            new Date(
                                                new Date().getFullYear() - 18,
                                                new Date().getMonth(),
                                                new Date().getDate(),
                                            )
                                        }
                                        minDate={new Date("1/1/1900")}
                                    />
                                    {age !== null && <span className="ageLabel">Age: {age}</span>}
                                </div>
                                {touched.dateOfBirth && errors.dateOfBirth && (
                                    <StyledError>{errors.dateOfBirth}</StyledError>
                                )}
                            </div>
                        </InlineFormRow>
                        <InlineFormRow className="row-three-quarter">
                            <div className="field-half">
                                <CopyableFieldWrapper>
                                    <InputCommon
                                        name="email"
                                        handleChange={handleChange}
                                        handleBlur={handleBlur}
                                        value={values.email}
                                        label="Email *"
                                        required
                                        type="text"
                                        placeHolder={
                                            ftEmail ? `Same as above: ${ftEmail}` : undefined
                                        }
                                    />
                                    {ftEmail && (
                                        <CopyFromAboveButton
                                            onClick={() => setFieldValue("email", ftEmail)}
                                        />
                                    )}
                                </CopyableFieldWrapper>
                            </div>
                            <div className="field-half">
                                <CopyableFieldWrapper>
                                    <div style={{ flex: 1 }}>
                                        <TextField
                                            name="phoneNumber"
                                            label="Phone"
                                            type="tel"
                                            required
                                            placeholder={
                                                ftPhone
                                                    ? `Same as above: ${ftPhone}`
                                                    : "+1 (234) 567-8900"
                                            }
                                            fullWidth
                                            variant="standard"
                                            sx={{ marginTop: "8px" }}
                                            value={raw}
                                            onKeyDown={handleKeyDown}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            slotProps={{
                                                htmlInput: {
                                                    inputMode: "tel" as const,
                                                    pattern: `(?=(?:.*\\d){4,})[0-9()+\\-\\s]+`,
                                                    title: "Enter at least 4 digits; only digits, spaces, (), +, - allowed",
                                                    onPaste: handlePhonePaste,
                                                },
                                                inputLabel: {
                                                    sx: {
                                                        "&.MuiInputLabel-shrink": {
                                                            fontWeight: 600,
                                                            fontSize: "11px",
                                                            transform:
                                                                "translate(0, -3px) scale(1)",
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        {touched.phoneNumber &&
                                            (isTooShort || errors.phoneNumber) && (
                                                <StyledError>
                                                    {isTooShort
                                                        ? "Please enter at least 4 digits."
                                                        : errors.phoneNumber}
                                                </StyledError>
                                            )}
                                    </div>
                                    {ftPhone && (
                                        <CopyFromAboveButton
                                            onClick={() => setFieldValue("phoneNumber", ftPhone)}
                                        />
                                    )}
                                </CopyableFieldWrapper>
                            </div>
                        </InlineFormRow>
                        <InlineFormRow className="row-three-quarter">
                            <div className="field-half">
                                <CopyableFieldWrapper>
                                    <InputCommon
                                        name="postalCode"
                                        handleChange={handleChange}
                                        handleBlur={handleBlur}
                                        value={values.postalCode}
                                        label="Post code *"
                                        required
                                        type="text"
                                        placeHolder={
                                            ftPostCode ? `Same as above: ${ftPostCode}` : undefined
                                        }
                                    />
                                    {ftPostCode && (
                                        <CopyFromAboveButton
                                            onClick={() => setFieldValue("postalCode", ftPostCode)}
                                        />
                                    )}
                                </CopyableFieldWrapper>
                            </div>
                            <div className="field-half">
                                <CopyableFieldWrapper>
                                    <SelectCommon
                                        name="countryCode"
                                        handleChange={(value) =>
                                            setFieldValue("countryCode", value)
                                        }
                                        handleBlur={handleBlur}
                                        value={values.countryCode}
                                        label="Country"
                                        required
                                        listValueMembers={countriesModel}
                                    />
                                    {ftCountryCode && (
                                        <CopyFromAboveButton
                                            onClick={() =>
                                                setFieldValue("countryCode", ftCountryCode)
                                            }
                                        />
                                    )}
                                </CopyableFieldWrapper>
                            </div>
                        </InlineFormRow>
                        {!hideButton && (
                            <InlineFormActions>
                                <BorderButton
                                    $borderRadius={theme.borderRadius.small}
                                    color={theme.colors.main}
                                    type="submit"
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        earnPointsRef.current = !!showEarnPointsActions;
                                    }}
                                >
                                    {isSubmitting
                                        ? "Saving ..."
                                        : showEarnPointsActions
                                          ? "Save and Earn Points"
                                          : "Save"}
                                </BorderButton>
                                {showEarnPointsActions && (
                                    <PlainSaveButton
                                        type="submit"
                                        disabled={isSubmitting}
                                        onClick={() => {
                                            earnPointsRef.current = false;
                                        }}
                                    >
                                        Continue without discounting
                                    </PlainSaveButton>
                                )}
                            </InlineFormActions>
                        )}
                    </InlineFormContainer>
                );
            }}
        </Formik>
    );
};

const InlineFormContainer = styled.form<{ $noBorder?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 8px 12px;
    border: ${({ $noBorder }) => ($noBorder ? "none" : `1px solid #e0e3e6`)};
    border-radius: ${theme.borderRadius.medium};
    background: #fff;
    margin-top: 4px;
`;

const BookingDetailsSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    padding-top: 17px;
    border-top: 1px solid #e0e3e6;
`;

const BookingDetailsSectionTitle = styled.h6`
    font-size: 13px;
    font-weight: 600;
    color: ${theme.colors.gray_1};
    margin: 0 0 4px 0;
    font-family: Poppins;
`;

const InlineFormRow = styled.div`
    display: flex;
    gap: 12px;
    align-items: flex-end;
    width: 100%;

    ${breakpoint.breakOnlyMobile`
        flex-direction: column;
        gap: 0;
    `}

    .MuiFormControl-root {
        margin-top: 8px !important;
    }

    .MuiFormControl-marginNormal {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
    }

    .field-title {
        flex: 0 0 90px;
        min-width: 80px;
        ${breakpoint.breakOnlyMobile`
            width: 100%;
            flex: unset;
        `}
    }

    .field-name {
        flex: 1;
        min-width: 0;
        ${breakpoint.breakOnlyMobile`
            width: 100%;
        `}
    }

    .field-dob {
        flex: 1;
        min-width: 0;
        ${breakpoint.breakOnlyMobile`
            width: 100%;
        `}
        .dobWrapper {
            display: flex;
            align-items: center;
            gap: 8px;
            .ageLabel {
                font-size: 14px;
                font-weight: 600;
                color: ${theme.colors.main};
                white-space: nowrap;
                padding-top: 16px;
            }
        }
    }

    .field-half {
        flex: 1;
        min-width: 0;
        ${breakpoint.breakOnlyMobile`
            width: 100%;
        `}
    }

    &.row-three-quarter {
        width: 75%;
        ${breakpoint.breakOnlyMobile`
            width: 100%;
        `}
    }
`;

const CopyableFieldWrapper = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 4px;
    flex: 1;
    min-width: 0;

    > *:first-child {
        flex: 1;
        min-width: 0;
    }
`;

const InlineFormActions = styled.div`
    display: flex;
    justify-content: flex-start;
    margin-top: 9px;
    padding-bottom: 4px;
    button {
        min-width: 140px;
    }
`;

const CheckoutTravelerComponent: React.FC<ICheckoutTravelerComponentProps> = ({
    quantityIndex,
    suggestionRedeemers,
    items,
    checkInOptionsMap,
    isLoadingBookingDetails,
    onItemsChange,
    onCreateNewCustomer,
    firstTravellerData,
    firstTravellerOptionalData,
    globalFirstTravellerData,
}) => {
    const itemsRef = useRef(items);
    itemsRef.current = items;

    const checkInDateItem = useMemo(() => getCheckInDateItem(items), [items])!;
    const fareData = useMemo(() => getCartItemFareData(checkInDateItem), [checkInDateItem]);
    const needsTimeDropdown = useMemo(
        () => checkBookingNeedsPickUpTimeID(fareData.bookingSystem),
        [fareData.bookingSystem],
    );
    const selectedTimeId = useMemo(() => {
        return checkInDateItem.booking_data?.find((bd) => bd.quantityIndex === quantityIndex)
            ?.timeId;
    }, [checkInDateItem, quantityIndex]);
    const numPax: number = useMemo(
        () => (_.isNil(checkInDateItem) ? 0 : getNumPax(checkInDateItem)),
        [checkInDateItem],
    );
    const redeemers = useMemo(
        () =>
            _.uniq(
                items
                    .flatMap((item) =>
                        item.booking_data
                            .filter((b) => b.quantityIndex === quantityIndex)
                            .flatMap((bd) => bd.redeemers || []),
                    )
                    .map((r) => r.toString()),
            ),
        [items, quantityIndex],
    );
    const travellers = useMemo(() => {
        return suggestionRedeemers.filter((c) => redeemers.includes(c.id.toString()));
    }, [suggestionRedeemers, redeemers]);

    /**
     * Everyone already on this product, across every traveller slot.
     *
     * Scoped to the whole product rather than the slot being filled: one person
     * cannot travel twice on the same booking, so offering them again in slot 2
     * after they were added to slot 1 only invites a duplicate the supplier would
     * have to untangle. `items` is this product's group of cart rows, so this is
     * exactly "per product".
     */
    const redeemersOnThisProduct = useMemo(
        () =>
            new Set(
                items
                    .flatMap((item) => item.booking_data.flatMap((bd) => bd.redeemers || []))
                    .map((r) => r.toString()),
            ),
        [items],
    );

    const filteredSuggestions = useMemo(
        () => suggestionRedeemers.filter((user) => !redeemersOnThisProduct.has(user.id.toString())),
        [suggestionRedeemers, redeemersOnThisProduct],
    );

    const checkInOptions = useMemo(
        () => checkInOptionsMap[checkInDateItem.id],
        [checkInOptionsMap, checkInDateItem.id],
    );
    const [timeOptions, setTimeOptions] = useState<{
        displayName: string;
        options: IPickupTimeOption[];
    } | null>(null);
    const [pickUpLocationOptions, setPickUpLocationOptions] = useState<{
        displayName: string;
        options: IPickUpLocationOption[];
    } | null>(null);
    const [dropOffLocationOptions, setDropOffLocationOptions] = useState<{
        displayName: string;
        options: IDropOffLocationOption[];
    } | null>(null);
    const [countriesData, setCountriesData] = useState<ICountryData[]>([]);
    const { account } = useAppSelector(selectAccount);
    const isLoggedIn = useIsLoggedIn();
    const hasShownPastTimeWarning = React.useRef(false);
    const [isInlineFormSubmitting, setIsInlineFormSubmitting] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(!isLoggedIn);
    const inlineFormSubmitRef = useRef<(() => void) | null>(null);

    const shouldAskPersonalDetails =
        !sessionHelper.hasSession() &&
        !(
            account?.title &&
            account?.first_name &&
            account?.last_name &&
            account?.email &&
            account?.phone_number &&
            account?.post_code &&
            account?.country_code
        );

    /**
     * Set while the form is open to correct a saved traveller's stand-in details
     * rather than to create a new one. Carries the record being corrected as well
     * as its seeded values, because the save has to know *which* traveller it is
     * fixing, and doubles as the flag that the form is in review mode.
     */
    const [travellerReview, setTravellerReview] = useState<
        { traveller: ICustomerDetail; prefill: Partial<ISetCustomerData> } | undefined
    >(undefined);
    const travellerEditPrefill = travellerReview?.prefill;

    const [isTravellerSugesstionOpen, setIsTravellerSugesstionOpen] = useState(false);
    const [isTravellerRemoveOpen, setIsTravellerRemoveOpen] = useState(false);
    const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
    const [isAddingTraveller, setIsAddingTraveller] = useState(false);
    const isAddingTravellerRef = useRef(false);

    const openTravellerSugesstionBox = () => {
        const qIdx = Number(quantityIndex);
        const existingRedeemers = _.chain(items)
            .flatMap("booking_data")
            .filter((bd: any) => Number(bd.quantityIndex) === qIdx)
            .flatMap((bd: any) => bd.redeemers || [])
            .map(Number)
            .compact()
            .uniq()
            .value();

        if (existingRedeemers.length >= numPax) {
            WarnToast({ Message: `Maximum ${numPax} traveller(s) reached` });
            return;
        }

        if (!isLoggedIn) {
            setIsFormOpen(true);
            return;
        }

        setIsTravellerSugesstionOpen(true);
    };
    const closeTravellerSugesstionBox = () => {
        setIsTravellerSugesstionOpen(false);
        setSelectedSuggestion(null);
    };
    const openTravellerRemoveBox = () => setIsTravellerRemoveOpen(true);
    const closeTravellerRemoveBox = () => {
        setIsTravellerRemoveOpen(false);
        setSelectedSuggestion(null);
    };

    const onSelectTraveller = async (
        selectedSuggestion: Suggestion | null,
        options?: { skipReviewGate?: boolean },
    ) => {
        if (!selectedSuggestion) return;
        // Guard against re-entry (e.g. double-tap "Add to booking"): the address-book
        // path creates a redeemer async, and a second run would create a duplicate.
        if (isAddingTravellerRef.current) return;
        isAddingTravellerRef.current = true;
        setIsAddingTraveller(true);

        try {
            // Enforced here as well as by hiding them from the suggestions: the
            // same person must not end up on one product twice, whichever route
            // reached this. Skipped for an address-book pick, whose id is a
            // synthetic "ab:" one that becomes a fresh redeemer below.
            if (
                !isAddressBookSuggestionId(selectedSuggestion.id) &&
                redeemersOnThisProduct.has(selectedSuggestion.id.toString())
            ) {
                WarnToast({ Message: "That traveller is already on this booking" });
                closeTravellerSugesstionBox();
                return;
            }

            // A traveller still carrying the sign-up stand-ins cannot be assigned
            // as they are. Open the form on their details instead, so the required
            // fields have to be filled in before they can join the booking.
            const full = suggestionRedeemers.find((c) => c.id === selectedSuggestion.id);
            if (!options?.skipReviewGate && full && travellerNeedsDetailReview(full, account)) {
                setTravellerReview({
                    traveller: full,
                    prefill: travellerToReviewPrefill(full, account),
                });
                closeTravellerSugesstionBox();
                setIsFormOpen(true);
                return;
            }

            let effectiveId = selectedSuggestion.id;

            // Address-book-only travellers have no numeric redeemer id yet; create a
            // redeemer from the saved guest first, then assign the returned id.
            if (isAddressBookSuggestionId(selectedSuggestion.id)) {
                if (!full?.guest) return;
                try {
                    const created = await onCreateNewCustomer(guestToSetCustomerData(full.guest));
                    effectiveId = !_.isNil(created?.id) ? String(created.id) : "";
                } catch (error: any) {
                    if (error?.message === "Login cancelled") return;
                    ErrorToast({ Message: "Failed to add traveller" });
                    return;
                }
                if (!effectiveId) return;
            }

            const adjustedItems = items.map((item) => {
                const updatedBookingData = item.booking_data
                    .map((bd) => {
                        let redeemerId: number | null = null;
                        if (selectedSuggestion?.quoteId) {
                            const lastCustomer = _.last(selectedSuggestion.customers);
                            redeemerId = lastCustomer ? Number(lastCustomer.id) : null;
                        } else {
                            redeemerId = Number(effectiveId);
                        }
                        if (bd.quantityIndex === quantityIndex && redeemerId) {
                            return {
                                ...bd,
                                redeemers: _.uniq([...(bd.redeemers || []), redeemerId]),
                            };
                        }
                        return bd;
                    })
                    .filter((bd) => !(numPax > 1 && (!bd.redeemers || bd.redeemers.length === 0)));

                return {
                    ...item,
                    booking_data: updatedBookingData,
                };
            });

            onItemsChange(adjustedItems);
            closeTravellerSugesstionBox();
        } finally {
            isAddingTravellerRef.current = false;
            setIsAddingTraveller(false);
        }
    };

    const onRemoveTraveller = (travellerId: number) => {
        let shouldCloseSheet = false;
        const idToRemove = Number(travellerId);

        onItemsChange(
            items.map((item) => {
                return {
                    ...item,
                    booking_data: item.booking_data.map((bd) => {
                        if (bd.quantityIndex === quantityIndex) {
                            const filteredRedeemers = _.uniq(
                                (bd.redeemers ?? []).filter((r) => Number(r) !== idToRemove),
                            );

                            if (filteredRedeemers.length === 0) {
                                shouldCloseSheet = true;
                                return {
                                    timeId: bd.timeId,
                                    quantityIndex: bd.quantityIndex,
                                    redeemers: [],
                                };
                            }
                            return {
                                ...bd,
                                redeemers: filteredRedeemers,
                            };
                        }

                        return bd;
                    }),
                };
            }),
        );

        if (shouldCloseSheet) {
            closeTravellerRemoveBox();
        }
    };

    /**
     * Save the traveller, and for the first one optionally use those details to
     * become a points agent.
     *
     * The traveller form happens to collect exactly the six fields the backend's
     * upgrade gate requires (checkIfUserContainsLeadCustomerDetail: first/last
     * name, date of birth, phone, post code, country), which the account itself
     * usually lacks — so the details are copied onto the profile first, then the
     * upgrade is attempted, then the account and metadata are reloaded so the
     * points UI can appear.
     *
     * The upgrade can legitimately fail (TDMS rejects an email it already knows,
     * and a PeterPans-branch agent resolves as a default agent rather than a
     * points one). Those failures are surfaced but do not undo the save.
     */
    const travellerDispatch = useAppDispatch();
    const { setIsUpgradingPoints } = useCheckout();

    /**
     * The points offer only makes sense for the account holder who has an account
     * but is not yet earning points. A guest has no account to upgrade, and an
     * existing agent already earns — both see the plain save button.
     */
    const canEarnPoints = quantityIndex === 1 && isLoggedIn && !account?.is_agent_integrated;

    /**
     * True while the account still holds the stand-ins written by the sign-up
     * upgrade, so the first traveller's details are the first real values it has
     * ever been given.
     */
    const accountHasPlaceholders = hasPlaceholderProfile(account);

    /**
     * True while the account holds nothing real beyond the three fields sign-up
     * collected — the only state in which a stored title or country cannot have
     * been the visitor's own choice.
     */
    const accountHasOnlySignupDetails = hasOnlySignupDetails(account);

    // The first traveller is assumed to be the account holder, so seed the form
    // from the account — for someone who just signed up that is the name and
    // email they gave the offer sheet. Blank fields are left blank rather than
    // written as empty strings, so the spread never clears a real value.
    const accountPrefill = useMemo(() => {
        if (quantityIndex !== 1 || !account) return undefined;
        const seeded: Partial<ISetCustomerData> = {};
        // The names are stand-ins too now that sign-up does not ask for them, so
        // they are dropped on sight like the numbers below.
        if (account.first_name && !isPlaceholderName("first_name", account.first_name))
            seeded.firstName = account.first_name;
        if (account.last_name && !isPlaceholderName("last_name", account.last_name))
            seeded.lastName = account.last_name;
        if (account.email) seeded.email = account.email;
        // Presenting a stand-in would invite the visitor to accept a fake phone
        // number or post code as their own, so each is dropped where it is
        // recognisable — field by field, so an account that has been partly
        // corrected keeps whatever is already real.
        if (account.phone_number && !isPlaceholderPhone(account.phone_number))
            seeded.phoneNumber = account.phone_number;
        if (account.post_code && !isPlaceholderPostCode(account.post_code))
            seeded.postalCode = account.post_code;
        // Title and country are not recognisable — "Mr" and "AU" are ordinary
        // real answers — so they are dropped only when the account holds nothing
        // beyond the three fields sign-up collected, which is the one state in
        // which neither can have been chosen.
        if (!accountHasOnlySignupDetails) {
            if (account.title) seeded.title = account.title;
            if (account.country_code) seeded.countryCode = account.country_code;
        }
        return Object.keys(seeded).length > 0 ? seeded : undefined;
    }, [account, accountHasOnlySignupDetails, quantityIndex]);
    // Which of the two save buttons was pressed. A ref rather than state because
    // it is read inside the Formik submit that the button triggers, in the same
    // tick — state would still hold the previous value.
    const earnPointsOnSaveRef = useRef(false);

    const handleInlineFormSave = async (values: ISetCustomerData, earnPointsArg?: boolean) => {
        // The form's own buttons pass the choice explicitly; the outer buttons
        // (used when the form is rendered with hideButton) set the ref instead.
        const earnPoints = earnPointsArg ?? earnPointsOnSaveRef.current;
        const review = travellerReview;
        /**
         * Correcting the account holder's own row is a profile edit, not a new
         * traveller.
         *
         * The redeemer pool has no update endpoint — `PUT cart/redeemers` answers
         * 405 — and posting the same person again is refused as "a traveller with
         * this name and email already exists". So the existing redeemer is kept
         * and reassigned: an is_primary row reads its details from the account's
         * customer record, which the profile write below corrects, so reassigning
         * it picks up the real values.
         */
        const correctingAccountHolder = !!review?.traveller.is_primary;

        setIsInlineFormSubmitting(true);
        try {
            const createdCustomer = correctingAccountHolder
                ? undefined
                : await onCreateNewCustomer(values);

            // The upgrade is only for someone not yet earning points. The profile
            // write runs more widely: an account upgraded at sign-up is already an
            // agent, so it fails canEarnPoints, yet it is still carrying the
            // stand-ins and this save is the first chance to replace them. It is
            // also the only way to correct the account holder's own row.
            const shouldUpgrade = earnPoints && canEarnPoints;
            const shouldWriteProfile =
                shouldUpgrade ||
                correctingAccountHolder ||
                (quantityIndex === 1 && isLoggedIn && accountHasPlaceholders);
            let profileWriteFailed = false;

            if (shouldWriteProfile) {
                // Hold the Order Summary in its loading state for the whole
                // sequence — profile update, upgrade, account and metadata reload
                // — so the totals never sit there looking final while they are
                // about to change.
                setIsUpgradingPoints(true);
                try {
                    await accountApi.updateUserDetail({
                        title: values.title,
                        first_name: values.firstName,
                        last_name: values.lastName,
                        // Same format My Account sends; the raw form value is a
                        // different shape and would be stored wrong or rejected.
                        date_of_birth: values.dateOfBirth
                            ? moment(values.dateOfBirth).format("DD-MMM-YYYY")
                            : "",
                        email: values.email,
                        phone_number: values.phoneNumber,
                        post_code: values.postalCode,
                        country_code: values.countryCode,
                    });
                    if (shouldUpgrade) {
                        // Carry the referral through, as /link-agent-account does,
                        // so a referred sign-up keeps its attribution.
                        const referral = new URLSearchParams(window.location.search).get(
                            "referrer",
                        );
                        await pointsApi.upgradeToPointsAgent(referral || undefined);
                    }
                    // initializeAccountDetail is what the upgrade page uses to pick
                    // up is_agent_integrated; getMetaData refreshes the agent-type
                    // flags the points UI reads. Both run on a write-only pass too,
                    // so the reloaded account no longer looks like a bare sign-up
                    // and the form stops blanking the fields that are now real.
                    await travellerDispatch(initializeAccountDetail());
                    await travellerDispatch(getMetaData());
                    if (shouldUpgrade) {
                        SuccessToast({ Message: "You're now earning points on this booking" });
                    }
                } catch (error: any) {
                    profileWriteFailed = true;
                    ErrorToast({
                        Message:
                            error?.response?.data?.title ||
                            (shouldUpgrade
                                ? "Traveller saved, but we couldn't enable points on this account"
                                : "We couldn't save these details to your account"),
                    });
                } finally {
                    setIsUpgradingPoints(false);
                }
            }

            // Assigned only after the profile write, so a reassigned redeemer is
            // read back with the corrected details rather than the stand-ins. A
            // failed write leaves the form open to retry rather than putting the
            // stand-ins onto the booking.
            if (correctingAccountHolder) {
                if (profileWriteFailed || !review) return;
                await onSelectTraveller(review.traveller, { skipReviewGate: true });
            } else if (createdCustomer) {
                onSelectTraveller(createdCustomer);
            }

            setIsFormOpen(false);
            setTravellerReview(undefined);
        } catch (error: any) {
            if (error?.message === "Login cancelled") return;
            // Correcting a companion traveller cannot be saved: the redeemer
            // already exists under this name and email, and the pool has no update
            // endpoint to correct it through. Say so, rather than surfacing a bare
            // duplicate-key message against a form the visitor just filled in
            // correctly. Should not arise in practice — stand-ins only ever reach a
            // record via the account holder's own profile.
            const isDuplicateOnCorrection =
                !!review && !correctingAccountHolder && /already exists/i.test(error?.title ?? "");
            const validationMessage = isDuplicateOnCorrection
                ? "This traveller is already saved under this name and email, so their details have to be changed from your account."
                : error.data?.email?.[0] ||
                  error.data?.firstName?.[0] ||
                  error.data?.lastName?.[0] ||
                  error.title ||
                  "Failed to add traveller";
            ErrorToast({ Message: validationMessage });
        } finally {
            setIsInlineFormSubmitting(false);
        }
    };

    const onTimeChange = (timeIdStr: string | null) => {
        if (!timeIdStr) return;
        onItemsChange(
            items.map((item) => ({
                ...item,
                booking_data: item.booking_data.map((bd) => {
                    if (bd.quantityIndex === quantityIndex) {
                        return {
                            ...bd,
                            timeId: timeIdStr,
                            pickupId: undefined,
                            dropoffId: undefined,
                        };
                    }
                    return bd;
                }),
            })),
        );
    };

    const onPickUpLocationChange = (pickUpIdStr: string | null) => {
        if (!pickUpIdStr) return;
        const pickUpId = pickUpIdStr;
        const selectedPickUpLocation = pickUpLocationOptions?.options.find(
            (opt) => String(opt.id) === String(pickUpId),
        );

        if (!selectedPickUpLocation) return;

        if (isPickupTimeInPast(selectedPickUpLocation.displayName, checkInDateItem.booking_date)) {
            WarnToast({
                Message: "This pickup time is in the past. Please select a different time or date.",
            });
            return;
        }

        onItemsChange(
            items.map((item) => {
                return {
                    ...item,
                    booking_data: item.booking_data.map((bd) => {
                        if (bd.quantityIndex === quantityIndex) {
                            return {
                                ...bd,
                                pickupId: selectedPickUpLocation.id,
                                pickupLocation: selectedPickUpLocation.displayName,
                            };
                        }
                        return bd;
                    }),
                };
            }),
        );
    };
    const onDropOffChange = (dropoffId: string | null) => {
        if (!dropoffId) return;
        const dropOffLocation = dropOffLocationOptions?.options.find(
            (opt) => String(opt.id) === String(dropoffId),
        );
        onItemsChange(
            items.map((item) => {
                return {
                    ...item,
                    booking_data: item.booking_data.map((bd) => {
                        if (bd.quantityIndex === quantityIndex) {
                            return {
                                ...bd,
                                dropoffId: dropOffLocation?.id,
                                dropoffLocation: dropOffLocation?.displayName,
                            };
                        }
                        return bd;
                    }),
                };
            }),
        );
    };

    const onOptionalChange = (fieldId: number, value: string) => {
        onItemsChange(
            itemsRef.current.map((item) => {
                return {
                    ...item,
                    booking_data: item.booking_data.map((bd) => {
                        if (bd.quantityIndex === quantityIndex) {
                            return {
                                ...bd,
                                optionalData: {
                                    ...(bd.optionalData ?? {}),
                                    [fieldId]: value,
                                },
                            };
                        }
                        return bd;
                    }),
                };
            }),
        );
    };

    useEffect(() => {
        externalApi.getCountriesDate().then((res) => {
            setCountriesData(res.data);
        });
    }, []);

    const isPickupTimeInPast = (displayName: string, bookingDate: string): boolean => {
        let timeMatch = displayName.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);

        if (timeMatch) {
            const [, hours, minutes, period] = timeMatch;
            let hour = parseInt(hours, 10);
            if (period.toUpperCase() === "PM" && hour !== 12) hour += 12;
            if (period.toUpperCase() === "AM" && hour === 12) hour = 0;
            const pickupDateTime = moment(bookingDate).hour(hour).minute(parseInt(minutes, 10));
            return pickupDateTime.isBefore(moment());
        }

        timeMatch = displayName.match(/^(\d{1,2}):(\d{2})/);
        if (timeMatch) {
            const [, hours, minutes] = timeMatch;
            const hour = parseInt(hours, 10);
            const minute = parseInt(minutes, 10);
            const pickupDateTime = moment(bookingDate).hour(hour).minute(minute);
            return pickupDateTime.isBefore(moment());
        }

        return false;
    };

    useEffect(() => {
        if (checkInOptions) {
            const timeLabel =
                checkInOptions.pickupTimes.map((pt) => pt.displayName).at(0) ?? "Time";
            const allTimeOptions: IPickupTimeOption[] = checkInOptions.pickupTimes
                .flatMap((pt) => pt.options)
                .map((pt) => ({
                    id: pt.id,
                    displayName: pt.displayName,
                    isDefault: false,
                    downstreamTimeRef: pt.downstreamTimeRef,
                    numAvailable: pt.numAvailable,
                }));

            const filteredTimeOptions = allTimeOptions.filter(
                (opt) => !isPickupTimeInPast(opt.displayName, checkInDateItem.booking_date),
            );

            if (
                filteredTimeOptions.length === 0 &&
                allTimeOptions.length > 0 &&
                !hasShownPastTimeWarning.current
            ) {
                hasShownPastTimeWarning.current = true;
                WarnToast({
                    Message:
                        "All pickup times for this booking are in the past. Please select a different date.",
                });
            }

            setTimeOptions({ displayName: timeLabel, options: filteredTimeOptions });
        } else {
            setTimeOptions(null);
        }
    }, [checkInOptions, checkInDateItem.booking_date]);

    useEffect(() => {
        if (!timeOptions?.options.length) return;
        const currentTimeId = checkInDateItem.booking_data?.find(
            (bd) => bd.quantityIndex === quantityIndex,
        )?.timeId;
        const alreadyValid = timeOptions.options.some(
            (opt) => String(opt.id) === String(currentTimeId),
        );
        if (!alreadyValid) {
            const defaultTime = timeOptions.options[0];
            onItemsChange(
                items.map((item) => ({
                    ...item,
                    booking_data: item.booking_data.map((bd) => {
                        if (bd.quantityIndex === quantityIndex) {
                            return {
                                ...bd,
                                timeId: defaultTime.id.toString(),
                            };
                        }
                        return bd;
                    }),
                })),
            );
        }
    }, [timeOptions, quantityIndex]);

    useEffect(() => {
        const selectedTimeOption = timeOptions?.options.find(
            (opt) => String(opt.id) === String(selectedTimeId),
        );
        if (checkInOptions) {
            const pickupLocationLabel =
                checkInOptions.pickupLocations.map((pl) => pl.displayName).at(0) ??
                "Pickup Location";
            let pickupLocationOptions: IPickUpLocationOptionWithRef[] = [];

            let allPickupOptions: IPickUpLocationOptionWithRef[] = [];

            if (selectedTimeOption) {
                allPickupOptions = checkInOptions.pickupLocations
                    .filter((pl) => pl.upstreamTimeRef === selectedTimeOption.downstreamTimeRef)
                    .flatMap((pl) =>
                        pl.options.map((opt) => ({
                            id: opt.id,
                            displayName: opt.displayName,
                            isDefault: false,
                            upstreamTimeRef: pl.upstreamTimeRef,
                        })),
                    );
            } else {
                const pickupGroups = checkInOptions.pickupLocations ?? [];
                if (String(selectedTimeId) === "0" && timeOptions?.options?.length) {
                    const fallbackTime = timeOptions.options[0];
                    allPickupOptions = checkInOptions.pickupLocations
                        .filter((pl) => pl.upstreamTimeRef === fallbackTime.downstreamTimeRef)
                        .flatMap((pl) =>
                            pl.options.map((opt) => ({
                                id: opt.id,
                                displayName: opt.displayName,
                                isDefault: false,
                                upstreamTimeRef: pl.upstreamTimeRef,
                            })),
                        );
                } else {
                    const groupsWithNullRefs = pickupGroups.filter(
                        (pl) => pl.upstreamTimeRef === null,
                    );
                    if (groupsWithNullRefs.length) {
                        allPickupOptions = groupsWithNullRefs.flatMap((pl) =>
                            pl.options.map((opt) => ({
                                id: opt.id,
                                displayName: opt.displayName,
                                isDefault: false,
                                upstreamTimeRef: pl.upstreamTimeRef,
                            })),
                        );
                    }
                }
            }

            pickupLocationOptions = allPickupOptions.filter((opt) => {
                const isPast = isPickupTimeInPast(opt.displayName, checkInDateItem.booking_date);
                return !isPast;
            });
            const dropoffLocationLabel =
                checkInOptions.dropoffLocations.map((dl) => dl.displayName).at(0) ??
                "Dropoff Location";
            let dropoffLocationOptions: IDropOffLocationOption[] = [];
            if (selectedTimeOption) {
                dropoffLocationOptions = checkInOptions.dropoffLocations
                    .filter((dl) => dl.upstreamTimeRef === selectedTimeOption.downstreamTimeRef)
                    .flatMap((dl) =>
                        dl.options.map((opt) => ({
                            id: opt.id,
                            displayName: opt.displayName,
                            isDefault: false,
                            upstreamTimeRef: dl.upstreamTimeRef,
                        })),
                    );
            } else {
                const dropoffGroups = checkInOptions.dropoffLocations ?? [];
                if (String(selectedTimeId) === "0" && timeOptions?.options?.length) {
                    const fallbackTime = timeOptions.options[0];
                    dropoffLocationOptions = checkInOptions.dropoffLocations
                        .filter((dl) => dl.upstreamTimeRef === fallbackTime.downstreamTimeRef)
                        .flatMap((dl) =>
                            dl.options.map((opt) => ({
                                id: opt.id,
                                displayName: opt.displayName,
                                isDefault: false,
                                upstreamTimeRef: dl.upstreamTimeRef,
                            })),
                        );
                } else {
                    const groupsWithNullRefs = dropoffGroups.filter(
                        (dl) => dl.upstreamTimeRef === null,
                    );
                    if (groupsWithNullRefs.length) {
                        dropoffLocationOptions = groupsWithNullRefs.flatMap((dl) =>
                            dl.options.map((opt) => ({
                                id: opt.id,
                                displayName: opt.displayName,
                                isDefault: false,
                                upstreamTimeRef: dl.upstreamTimeRef,
                            })),
                        );
                    } else {
                        dropoffLocationOptions = [];
                    }
                }
            }
            setPickUpLocationOptions({
                displayName: pickupLocationLabel,
                options: pickupLocationOptions,
            });
            setDropOffLocationOptions({
                displayName: dropoffLocationLabel,
                options: dropoffLocationOptions,
            });
        } else {
            setPickUpLocationOptions(null);
            setDropOffLocationOptions(null);
        }
    }, [checkInOptions, timeOptions, selectedTimeId]);

    const hasTraveller = travellers.length > 0;
    const showInlineForm = !hasTraveller && !isLoggedIn;
    const hasTimeOptions = needsTimeDropdown && timeOptions && timeOptions.options.length > 0;
    const hasLocationOptions =
        (pickUpLocationOptions && pickUpLocationOptions.options.length > 0) ||
        (dropOffLocationOptions && dropOffLocationOptions.options.length > 0);
    const hasOptionalFields = (checkInDateItem.booking_details.optionalFields?.length ?? 0) > 0;
    const hasBookingDetails = hasTimeOptions || hasLocationOptions || hasOptionalFields;

    useEffect(() => {
        if (!pickUpLocationOptions?.options.length) return;
        const currentPickupId = checkInDateItem.booking_data.find(
            (bd) => bd.quantityIndex === quantityIndex,
        )?.pickupId;
        const alreadyValid =
            currentPickupId &&
            pickUpLocationOptions.options.some((opt) => String(opt.id) === String(currentPickupId));
        if (!alreadyValid) {
            const defaultPickup = pickUpLocationOptions.options[0];
            if (
                defaultPickup &&
                !isPickupTimeInPast(defaultPickup.displayName, checkInDateItem.booking_date)
            ) {
                onPickUpLocationChange(defaultPickup.id.toString());
            }
        }
    }, [pickUpLocationOptions, quantityIndex]);

    useEffect(() => {
        if (!dropOffLocationOptions?.options.length) return;
        const currentDropoffId = checkInDateItem.booking_data.find(
            (bd) => bd.quantityIndex === quantityIndex,
        )?.dropoffId;
        const alreadyValid =
            currentDropoffId &&
            dropOffLocationOptions.options.some(
                (opt) => String(opt.id) === String(currentDropoffId),
            );
        if (!alreadyValid) {
            const defaultDropoff = dropOffLocationOptions.options[0];
            if (defaultDropoff) {
                onDropOffChange(defaultDropoff.id.toString());
            }
        }
    }, [dropOffLocationOptions, quantityIndex]);

    const handleSelectSuggestion = (s: Suggestion) =>
        setSelectedSuggestion((prev) => (prev?.id === s.id ? null : s));

    return (
        <>
            <TravelerContainer>
                <div className="listCount">{quantityIndex}</div>
                {hasTraveller ? (
                    <Traveller onClick={openTravellerRemoveBox}>
                        <div
                            style={{
                                display: "flex",
                                flex: 1,
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            {/* Resolved rather than read off the row: the account
                                holder's redeemer keeps whatever name it was created
                                with, so a profile corrected since would still show
                                here as the sign-up stand-in. */}
                            {travellers
                                .map((t) => {
                                    const d = resolveTravellerDetails(t, account);
                                    return [d.firstName, d.lastName].filter(Boolean).join(" ");
                                })
                                .join(", ")}{" "}
                            <StyledArrow $isOpen={isTravellerRemoveOpen} />
                        </div>
                    </Traveller>
                ) : !showInlineForm ? (
                    <Traveller onClick={openTravellerSugesstionBox}>
                        <div className="addTraveller">
                            <img src="/images/Add.svg" alt="add" />
                            {/* Only the first slot, and only for someone not already
                                earning points — the saving comes from the upgrade the
                                first traveller's details make possible. */}
                            <h3>
                                {canEarnPoints
                                    ? "Add Traveller(s) and Earn Points!"
                                    : "Add Traveller(s)"}
                            </h3>
                        </div>
                    </Traveller>
                ) : (
                    <TravellerLabel>Traveller Details</TravellerLabel>
                )}
            </TravelerContainer>

            {showInlineForm && isFormOpen && (
                <InlineFormContainer as="div">
                    <InlineTravellerForm
                        countriesData={countriesData}
                        onSave={handleInlineFormSave}
                        isSubmitting={isInlineFormSubmitting}
                        firstTravellerData={firstTravellerData}
                        globalFirstTravellerData={globalFirstTravellerData}
                        submitRef={inlineFormSubmitRef}
                        accountPrefill={travellerEditPrefill ?? accountPrefill}
                        hideButton
                        noBorder
                    />
                    {(hasBookingDetails || isLoadingBookingDetails) && (
                        <BookingDetailsSection>
                            <BookingDetailsSectionTitle>
                                Required Booking Details
                            </BookingDetailsSectionTitle>
                            {isLoadingBookingDetails && !hasLocationOptions && !hasTimeOptions && (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        padding: "8px 0",
                                    }}
                                >
                                    <ClipLoaderCommon
                                        color={theme.colors.main}
                                        size={16}
                                        margin={0}
                                    />
                                    <span style={{ fontSize: 13, color: theme.colors.gray_1 }}>
                                        Loading booking details...
                                    </span>
                                </div>
                            )}
                            {needsTimeDropdown && timeOptions && timeOptions.options.length > 1 && (
                                <Dropdown
                                    key={`time-${quantityIndex}`}
                                    options={timeOptions.options.map((o) => ({
                                        label:
                                            o.numAvailable !== undefined && o.numAvailable !== null
                                                ? `${o.displayName} (${o.numAvailable} available)`
                                                : o.displayName,
                                        value: o.id.toString(),
                                    }))}
                                    placeholder={timeOptions.displayName}
                                    onChange={onTimeChange}
                                    value={selectedTimeId?.toString() ?? ""}
                                />
                            )}
                            {needsTimeDropdown &&
                                timeOptions &&
                                timeOptions.options.length === 1 && (
                                    <div
                                        style={{
                                            fontSize: 14,
                                            padding: "8px 0",
                                            color: theme.colors.gray_1,
                                        }}
                                    >
                                        <strong>{timeOptions.displayName}:</strong>{" "}
                                        {timeOptions.options[0].displayName}
                                        {timeOptions.options[0].numAvailable !== undefined &&
                                            timeOptions.options[0].numAvailable !== null && (
                                                <span>
                                                    {" "}
                                                    ({timeOptions.options[0].numAvailable}{" "}
                                                    available)
                                                </span>
                                            )}
                                    </div>
                                )}
                            {pickUpLocationOptions && pickUpLocationOptions.options.length > 0 && (
                                <div>
                                    <Dropdown
                                        key={`pickup-${quantityIndex}`}
                                        options={pickUpLocationOptions.options.map((o) => ({
                                            label: o.displayName,
                                            value: o.id.toString(),
                                        }))}
                                        placeholder={pickUpLocationOptions.displayName}
                                        onChange={onPickUpLocationChange}
                                        disabled={pickUpLocationOptions.options.length === 0}
                                        value={
                                            checkInDateItem.booking_data
                                                .find((bd) => bd.quantityIndex === quantityIndex)
                                                ?.pickupId?.toString() ?? ""
                                        }
                                    />
                                    {pickUpLocationOptions.options.length === 0 && (
                                        <ErrorMessage>
                                            All pickup times have passed. Choose another date.
                                        </ErrorMessage>
                                    )}
                                </div>
                            )}
                            {dropOffLocationOptions &&
                                dropOffLocationOptions.options.length > 0 && (
                                    <Dropdown
                                        key={`dropoff-${quantityIndex}`}
                                        options={dropOffLocationOptions.options.map((o) => ({
                                            label: o.displayName,
                                            value: o.id.toString(),
                                        }))}
                                        placeholder={dropOffLocationOptions.displayName}
                                        onChange={onDropOffChange}
                                        value={
                                            checkInDateItem.booking_data.find(
                                                (bd) => bd.quantityIndex === quantityIndex,
                                            )?.dropoffId || ""
                                        }
                                    />
                                )}
                            {(() => {
                                const allFields =
                                    checkInDateItem.booking_details.optionalFields ?? [];
                                const hiddenWhenInlineForm = [
                                    "Mobile",
                                    "Email",
                                    "Postcode",
                                    "Country",
                                ];
                                const fields = allFields.filter(
                                    (f) => !hiddenWhenInlineForm.includes(f.commonName),
                                );
                                const ageIndex = fields.findIndex((f) => f.commonName === "Age");
                                const hasDob = !!(
                                    travellers?.[0]?.date_of_birth ||
                                    travellers?.[0]?.customer?.date_of_birth ||
                                    travellers?.[0]?.guest?.date_of_birth
                                );
                                const focusIndex =
                                    ageIndex >= 0 && hasDob && ageIndex + 1 < fields.length
                                        ? ageIndex + 1
                                        : -1;
                                const autoPopulatedFields = [
                                    "Age",
                                    "Email",
                                    "Mobile",
                                    "Postcode",
                                    "Country",
                                    "Gender",
                                ];
                                return fields.map((of, idx) => {
                                    const firstVal =
                                        firstTravellerOptionalData?.[of.optionalFieldId];
                                    const canCopy =
                                        !!firstVal && !autoPopulatedFields.includes(of.commonName);
                                    return (
                                        <CopyableFieldWrapper key={of.optionalFieldId}>
                                            <OptionalDataField
                                                optionalField={of}
                                                value={
                                                    checkInDateItem.booking_data.find(
                                                        (bd) => bd.quantityIndex === quantityIndex,
                                                    )?.optionalData?.[of.optionalFieldId] || ""
                                                }
                                                countriesData={countriesData}
                                                traveller={travellers}
                                                onChange={(v) =>
                                                    onOptionalChange(of.optionalFieldId, v)
                                                }
                                                autoFocus={idx === focusIndex}
                                            />
                                            {canCopy && (
                                                <CopyFromAboveButton
                                                    onClick={() =>
                                                        onOptionalChange(
                                                            of.optionalFieldId,
                                                            firstVal,
                                                        )
                                                    }
                                                />
                                            )}
                                        </CopyableFieldWrapper>
                                    );
                                });
                            })()}
                        </BookingDetailsSection>
                    )}
                    <InlineFormActions>
                        <BorderButton
                            $borderRadius={theme.borderRadius.small}
                            color={theme.colors.main}
                            type="button"
                            disabled={isInlineFormSubmitting}
                            onClick={() => {
                                earnPointsOnSaveRef.current = true;
                                inlineFormSubmitRef.current?.();
                            }}
                        >
                            {isInlineFormSubmitting
                                ? "Saving ..."
                                : canEarnPoints
                                  ? "Save and Earn Points"
                                  : "Save"}
                        </BorderButton>
                        {canEarnPoints && (
                            <PlainSaveButton
                                type="button"
                                disabled={isInlineFormSubmitting}
                                onClick={() => {
                                    earnPointsOnSaveRef.current = false;
                                    inlineFormSubmitRef.current?.();
                                }}
                            >
                                Continue without discounting
                            </PlainSaveButton>
                        )}
                    </InlineFormActions>
                </InlineFormContainer>
            )}

            {!showInlineForm && (hasBookingDetails || isLoadingBookingDetails) && (
                <TravelerInfo>
                    <h6>Booking Details</h6>
                    {isLoadingBookingDetails && !hasLocationOptions && !hasTimeOptions && (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "8px 0",
                            }}
                        >
                            <ClipLoaderCommon color={theme.colors.main} size={16} margin={0} />
                            <span style={{ fontSize: 13, color: theme.colors.gray_1 }}>
                                Loading booking details...
                            </span>
                        </div>
                    )}
                    {needsTimeDropdown && timeOptions && timeOptions.options.length > 1 && (
                        <Dropdown
                            key={`time-${quantityIndex}`}
                            options={timeOptions.options.map((o) => ({
                                label:
                                    o.numAvailable !== undefined && o.numAvailable !== null
                                        ? `${o.displayName} (${o.numAvailable} available)`
                                        : o.displayName,
                                value: o.id.toString(),
                            }))}
                            placeholder={timeOptions.displayName}
                            onChange={onTimeChange}
                            value={selectedTimeId?.toString() ?? ""}
                        />
                    )}
                    {needsTimeDropdown && timeOptions && timeOptions.options.length === 1 && (
                        <div style={{ fontSize: 14, padding: "8px 0", color: theme.colors.gray_1 }}>
                            <strong>{timeOptions.displayName}:</strong>{" "}
                            {timeOptions.options[0].displayName}
                            {timeOptions.options[0].numAvailable !== undefined &&
                                timeOptions.options[0].numAvailable !== null && (
                                    <span> ({timeOptions.options[0].numAvailable} available)</span>
                                )}
                        </div>
                    )}
                    {pickUpLocationOptions && pickUpLocationOptions.options.length > 0 && (
                        <div>
                            <Dropdown
                                key={`pickup-${quantityIndex}`}
                                options={pickUpLocationOptions.options.map((o) => ({
                                    label: o.displayName,
                                    value: o.id.toString(),
                                }))}
                                placeholder={pickUpLocationOptions.displayName}
                                onChange={onPickUpLocationChange}
                                disabled={pickUpLocationOptions.options.length === 0}
                                value={
                                    checkInDateItem.booking_data
                                        .find((bd) => bd.quantityIndex === quantityIndex)
                                        ?.pickupId?.toString() ?? ""
                                }
                            />
                            {pickUpLocationOptions.options.length === 0 && (
                                <ErrorMessage>
                                    All pickup times have passed. Choose another date.
                                </ErrorMessage>
                            )}
                        </div>
                    )}
                    {dropOffLocationOptions && dropOffLocationOptions.options.length > 0 && (
                        <Dropdown
                            key={`dropoff-${quantityIndex}`}
                            options={dropOffLocationOptions.options.map((o) => ({
                                label: o.displayName,
                                value: o.id.toString(),
                            }))}
                            placeholder={dropOffLocationOptions.displayName}
                            onChange={onDropOffChange}
                            value={
                                checkInDateItem.booking_data.find(
                                    (bd) => bd.quantityIndex === quantityIndex,
                                )?.dropoffId || ""
                            }
                        />
                    )}
                    {(() => {
                        const allFields = checkInDateItem.booking_details.optionalFields ?? [];
                        const ageIndex = allFields.findIndex((f) => f.commonName === "Age");
                        const hasDob = !!(
                            travellers?.[0]?.date_of_birth ||
                            travellers?.[0]?.customer?.date_of_birth ||
                            travellers?.[0]?.guest?.date_of_birth
                        );
                        const focusIndex =
                            ageIndex >= 0 && hasDob && ageIndex + 1 < allFields.length
                                ? ageIndex + 1
                                : -1;
                        const autoPopulatedFields = [
                            "Age",
                            "Email",
                            "Mobile",
                            "Postcode",
                            "Country",
                            "Gender",
                        ];
                        return allFields.map((of, idx) => {
                            const firstVal = firstTravellerOptionalData?.[of.optionalFieldId];
                            const canCopy =
                                !!firstVal && !autoPopulatedFields.includes(of.commonName);
                            return (
                                <CopyableFieldWrapper key={of.optionalFieldId}>
                                    <OptionalDataField
                                        optionalField={of}
                                        value={
                                            checkInDateItem.booking_data.find(
                                                (bd) => bd.quantityIndex === quantityIndex,
                                            )?.optionalData?.[of.optionalFieldId] || ""
                                        }
                                        countriesData={countriesData}
                                        traveller={travellers}
                                        onChange={(v) => onOptionalChange(of.optionalFieldId, v)}
                                        autoFocus={idx === focusIndex}
                                    />
                                    {canCopy && (
                                        <CopyFromAboveButton
                                            onClick={() =>
                                                onOptionalChange(of.optionalFieldId, firstVal)
                                            }
                                        />
                                    )}
                                </CopyableFieldWrapper>
                            );
                        });
                    })()}
                </TravelerInfo>
            )}

            {numPax > 1 && hasTraveller && (
                <div style={{ alignSelf: "flex-end", marginTop: "10px" }}>
                    <button
                        style={{ fontSize: "14px", color: theme.colors.main, fontWeight: "600" }}
                        onClick={openTravellerSugesstionBox}
                    >
                        +Add
                    </button>
                </div>
            )}
            {isLoggedIn && (
                <TravellerSuggestionsBox
                    isOpen={isTravellerSugesstionOpen}
                    onClose={closeTravellerSugesstionBox}
                    suggestions={filteredSuggestions}
                    selectedSuggestion={selectedSuggestion}
                    onSelect={handleSelectSuggestion}
                    onAdd={() => onSelectTraveller(selectedSuggestion)}
                    isAdding={isAddingTraveller}
                    countriesData={countriesData}
                    shouldAskPersonalDetails={shouldAskPersonalDetails}
                    onCreateNew={() => {
                        setIsTravellerSugesstionOpen(false);
                        setTravellerReview(undefined);
                        setIsFormOpen(true);
                    }}
                    needsDetailReview={(t) => travellerNeedsDetailReview(t, account)}
                    account={account}
                />
            )}

            {isLoggedIn && isFormOpen && (!hasTraveller || !!travellerEditPrefill) && (
                <InlineTravellerForm
                    // Remount when the seeded values change: Formik reads
                    // initialValues once, on mount, so switching between creating
                    // a traveller and correcting one would otherwise keep the
                    // previous form state.
                    key={travellerEditPrefill ? "review" : "create"}
                    countriesData={countriesData}
                    onSave={handleInlineFormSave}
                    isSubmitting={isInlineFormSubmitting}
                    firstTravellerData={firstTravellerData}
                    globalFirstTravellerData={globalFirstTravellerData}
                    showEarnPointsActions={canEarnPoints && !travellerEditPrefill}
                    accountPrefill={travellerEditPrefill ?? accountPrefill}
                />
            )}

            <TravellerRemoveBox
                isOpen={isTravellerRemoveOpen}
                onClose={closeTravellerRemoveBox}
                suggestions={travellers}
                onRemove={onRemoveTraveller}
                countriesData={countriesData}
                account={account}
            />
        </>
    );
};

const TravelerContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;

    .listCount {
        width: 32px;
        height: 32px;
        line-height: 30px;
        text-align: center;
        background-color: ${theme.colors.main};
        border-radius: 50%;
        font-weight: 600;
    }
`;

const Traveller = styled.div`
    flex: 1;
    border: 1px solid #c3c9d0;
    border-radius: ${theme.borderRadius.medium};
    gap: 10px;
    padding: 8px 20px;
    color: ${theme.colors.black};
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-family: Plus Jakarta Sans;
    height: 48px;

    .addTraveller {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        h3 {
            font-size: 16px;
            font-weight: 600;
        }
    }
`;

const TravellerLabel = styled.div`
    flex: 1;
    font-size: 16px;
    font-weight: 600;
    color: ${theme.colors.main};
    font-family: Plus Jakarta Sans;
    padding: 8px 0;
`;

const TravelerInfo = styled.div`
    margin-left: 40px;
    display: flex;
    gap: 8px;
    flex-direction: column;
    margin-top: 10px;
    h6 {
        font-size: 14px;
    }
`;

const StyledArrow = styled(KeyboardArrowDown)<{ $isOpen: boolean }>`
    transition: transform 0.5s;
    transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0deg)")};
    color: ${theme.colors.main};
`;

const ErrorMessage = styled.div`
    color: ${theme.colors.red};
    font-size: 12px;
    margin-top: 4px;
    font-family: Poppins;
    padding-left: 4px;
`;

interface ICartItemsByGroupProps extends ICartItemsByGroup {
    productClass: string;
    customers: ICustomerDetail[];
    suggestionRedeemers: ICustomerDetail[];
    checkInOptionsMap: { [key: string]: IBookingCheckInOptions };
    isLoadingBookingDetails: boolean;
    onItemsChange: (items: ICartItemData[]) => void;
    onCreateNewCustomer: (customer: ISetCustomerData) => Promise<ICustomerDetail>;
    group_id: string | null;
    globalFirstTravellerData?: ICustomerDetail;
}

const CartItemGroupComponent = (props: ICartItemsByGroupProps) => {
    const { items, productClass, suggestionRedeemers } = props;
    const PpdId = items[0]?.product_price_details_id;
    const matchingFare = items[0]?.product?.json?.faresprices?.find(
        (fp) => Number(fp.productPricesDetailsId) === Number(PpdId),
    );
    const numPax = matchingFare ? Number(matchingFare.numPax) || 1 : 1;
    const rawbookingQuantity = Math.max(...items.map((i) => i.booking_quantity));
    const bookingQuantity = rawbookingQuantity / numPax;

    const firstTravellerData = useMemo(() => {
        const redeemers = _.uniq(
            items
                .flatMap((item) =>
                    item.booking_data
                        .filter((b) => b.quantityIndex === 1)
                        .flatMap((bd) => bd.redeemers || []),
                )
                .map((r) => r.toString()),
        );
        return suggestionRedeemers.find((c) => redeemers.includes(c.id.toString()));
    }, [items, suggestionRedeemers]);

    const firstTravellerOptionalData = useMemo(() => {
        const checkInDateItem = getCheckInDateItem(items);
        if (!checkInDateItem) return undefined;
        return checkInDateItem.booking_data.find((bd) => bd.quantityIndex === 1)?.optionalData as
            | Record<number, string>
            | undefined;
    }, [items]);

    return (
        <CartItemRowContainer>
            <DateText>
                {formatBookingDatesByProductClass(
                    items.map((i) => i.booking_date),
                    productClass,
                )}
            </DateText>
            {_.range(1, bookingQuantity + 1).map((quantityIndex) => {
                return (
                    <div
                        key={quantityIndex}
                        // Lets a failed validation find this block again — see
                        // travellerFieldFocus.ts. One block can cover several cart
                        // items (the same product across dates), hence the list.
                        {...{
                            [TRAVELLER_ITEMS_ATTR]: travellerItemsAttrValue(
                                items.map((item) => item.id),
                            ),
                            [TRAVELLER_INDEX_ATTR]: String(quantityIndex),
                        }}
                    >
                        <CheckoutTravelerComponent
                            {...props}
                            quantityIndex={quantityIndex}
                            suggestionRedeemers={suggestionRedeemers}
                            firstTravellerData={quantityIndex > 1 ? firstTravellerData : undefined}
                            firstTravellerOptionalData={
                                quantityIndex > 1 ? firstTravellerOptionalData : undefined
                            }
                            globalFirstTravellerData={props.globalFirstTravellerData}
                        />
                    </div>
                );
            })}
        </CartItemRowContainer>
    );
};

const CartItemRowContainer = styled.div`
    /* Flags the traveller block that failed validation; applied and removed by
       focusInvalidTravellerField. */
    [${TRAVELLER_ITEMS_ATTR}].${TRAVELLER_INVALID_CLASS} {
        border: 2px solid ${(p) => p.theme.colors.red};
        border-radius: 6px;
        padding: 8px;
        transition: border-color 0.3s ease;
    }

    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 10px;
`;

const DateText = styled.p`
    font-size: 12px;
    font-weight: 600;
    font-family: Plus Jakarta Sans;
    color: #232b2b;
    margin: 4px 0px 8px 0px;
    text-decoration: underline;
    text-underline-offset: 3px;
`;

interface ICartItemsByFareProps extends ICartItemsByFare {
    productClass: string;
    customers: ICustomerDetail[];
    suggestionRedeemers: ICustomerDetail[];
    checkInOptionsMap: { [key: string]: IBookingCheckInOptions };
    isLoadingBookingDetails: boolean;
    onItemsChange: (items: ICartItemData[]) => void;
    onCreateNewCustomer: (customer: ISetCustomerData) => Promise<ICustomerDetail>;
    globalFirstTravellerData?: ICustomerDetail;
}

const CartItemsByFareComponent = (props: ICartItemsByFareProps) => {
    return (
        <div>
            <div className="fareName">{props.name}</div>
            {props.itemsByGroup.map((itemByGroup, groupIndex) => {
                return (
                    <>
                        <CartItemGroupComponent
                            key={groupIndex}
                            {...props}
                            group_id={itemByGroup.group_id}
                            items={itemByGroup.items}
                            customers={props.customers}
                            suggestionRedeemers={props.suggestionRedeemers}
                        />{" "}
                    </>
                );
            })}
        </div>
    );
};

interface ICartItemsByProductProps extends ICartItemsByProduct {
    customers: ICustomerDetail[];
    suggestionRedeemers: ICustomerDetail[];
    onItemsChange: (items: ICartItemData[]) => void;
    onCreateNewCustomer: (customer: ISetCustomerData) => Promise<ICustomerDetail>;
    checkInOptionsMap: { [key: string]: IBookingCheckInOptions };
    isLoadingBookingDetails: boolean;
    onValidateBeforeSubmit?: () => boolean;
}

const CartItemsByProduct = (props: ICartItemsByProductProps) => {
    const globalFirstTravellerData = useMemo(() => {
        const allItems = props.itemsByFare.flatMap((f) => f.itemsByGroup.flatMap((g) => g.items));
        const redeemers = _.uniq(
            allItems
                .flatMap((item) =>
                    item.booking_data
                        .filter((b) => b.quantityIndex === 1)
                        .flatMap((bd) => bd.redeemers || []),
                )
                .map((r) => r.toString()),
        );
        return props.suggestionRedeemers.find((c) => redeemers.includes(c.id.toString()));
    }, [props.itemsByFare, props.suggestionRedeemers]);

    return (
        <Info className="infoWrapper">
            <div className="info">
                <ItemsByProductHeader {...props} showDeleteButton={false} />
                <Border />
                {props.itemsByFare.map((itemByFare, fareIndex) => {
                    return (
                        <CartItemsByFareComponent
                            key={fareIndex}
                            {...props}
                            id={itemByFare.id}
                            name={itemByFare.name}
                            itemsByGroup={itemByFare.itemsByGroup}
                            bookingSystem={itemByFare.bookingSystem}
                            customers={props.customers}
                            suggestionRedeemers={props.suggestionRedeemers}
                            globalFirstTravellerData={globalFirstTravellerData}
                        />
                    );
                })}
            </div>
        </Info>
    );
};

export const Info = styled.div`
    background-color: ${(props) => props.theme.colors.gray_6};
    padding-bottom: 0 !important;
    margin-bottom: 16px;
    .info {
        display: flex;
        flex-direction: column;
        background-color: #fff;
        padding: 16px;
        border-radius: ${theme.borderRadius.medium};
        .main {
            display: flex;
            gap: 12px;
        }
        .img {
            position: relative;
            cursor: pointer;
            height: 64px;
            width: 64px;
            display: flex;
            align-items: center;
            justify-content: center;
            img {
                height: 100%;
                width: 100%;
                object-fit: cover;
                border-radius: ${theme.borderRadius.medium};
            }
        }
        .info-container {
            display: flex;
            justify-content: space-between;
            width: 90%;
            ${breakpoint.breakOnlyMobile`
                width: auto;
            `}
        }
        .price-container {
            border: 1px solid #c3c9d0;
            border-radius: ${theme.borderRadius.medium};
            display: flex;
            justify-content: space-between;
            padding: 8px 20px;
            align-items: center;
            .price {
                display: flex;
                flex-direction: column;
                color: #6e7375;
                font-size: 12px;
                span {
                    color: #000;
                    font-size: 18px;
                }
            }
            .quantity {
                color: #6e7375;
                font-size: 12px;
                display: flex;
                align-items: center;
                gap: 10px;
                span {
                    font-size: 20px;
                    color: #1f2937;
                    width: 18px;
                    text-align: center;
                }
            }
        }
        .fareName {
            font-size: 18px;
            font-weight: 600;
        }
    }
    .name-container {
        display: flex;
        flex-direction: column;
        gap: 6px;
        .title {
            width: 100%;
            font-size: 14px;
            font-weight: 600;
            line-height: 23px;
            cursor: pointer;
            color: #131214;
            text-decoration: underline;
            text-underline-offset: 1px;

            @media (min-width: 375px) {
                width: 200px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            @media (min-width: 425px) {
                width: 250px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            ${breakpoint.breakLarge`
                font-size: 18px;
                width: auto;
                overflow: visible;
                text-overflow: clip;
                white-space: normal;
            `}
        }
        .sub-title {
            color: #6e7375;
            font-size: 12px;
            line-height: 14px;
            @media (min-width: 375px) {
                width: 200px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            @media (min-width: 425px) {
                width: 250px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            ${breakpoint.breakLarge`
                width: auto;
                overflow: visible;
                text-overflow: clip;
                white-space: normal;
            `}
        }
        .location {
            display: flex;
            align-items: center;
            gap: 6px;
            color: #3c3c3c;
            font-size: 12px;
            font-weight: 500;
        }
    }
`;

export const Border = styled.div`
    border-top: 1px solid #e6e9eb;
    margin: 10px 0px 14px 0px;
`;

interface IPaymentOptionProps {
    isOpen: boolean;
    onClose: () => void;
    handleOrderCreate: (paymentType: string) => Promise<void>;
    customers: ICustomerDetail[];
    onOrderCreateSuccess?: () => void;
}

export const PaymentOptionBottomSheet = (props: IPaymentOptionProps) => {
    return (
        <>
            <PaymentOverlay hidden={!props.isOpen} onClick={props.onClose} />
            <PaymentBottomSheet>
                <PaymentOptionComponent {...props} />
            </PaymentBottomSheet>
        </>
    );
};

export const PaymentOptionBottomSheetDesktopView = (props: IPaymentOptionProps) => {
    return (
        <PaymentPopup onClick={props.onClose}>
            <Popup onClick={(e) => e.stopPropagation()}>
                <PaymentOptionComponent {...props} />
            </Popup>
        </PaymentPopup>
    );
};

const PaymentPopup = styled.div`
    ${breakpoint.breakOnlyMobile`
        display: none;
    `}
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 15;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Popup = styled.div`
    background: #fff;
    border-radius: ${theme.borderRadius.medium};
    padding: 30px 30px 0px 30px;
    width: 700px;
`;

const paymentSlideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const PaymentOverlay = styled.div`
    display: none;
    ${[breakpoint.breakMobile, breakpoint.breakSmall].map(
        (breakpointFn) => breakpointFn`
    display: block;`,
    )}
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 10;
    cursor: pointer;
`;

const PaymentBottomSheet = styled.div`
    display: none;
    ${[breakpoint.breakMobile, breakpoint.breakSmall].map(
        (breakpointFn) => breakpointFn`
        display: block;
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        z-index: 15;
        background: #fff;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
        animation: ${paymentSlideUp} 0.3s ease-out;
        padding: 16px 16px 0px 16px;

        .details-container{
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-bottom: 16px;
            h2 {
                font-size: 20px;
                font-weight: 600;
            }
            .details {
                display : flex;
                align-items : center;
                gap : 16px;
                div{
                    display : flex;
                    flex-direction : column;
                    gap : 6px;
                    h1{
                        font-size : 24px;
                        font-weight : 600;  
                    }
                    p{
                        font-size : 12px;
                        color : #6E7375;
                    }
                }
            }
            
        }
    `,
    )}
`;

/* Pushed to opposite edges of the container on desktop, stacked full width on
   mobile. Source order puts Save first, so it takes the left edge and the way
   back the right, and below it once the row becomes a column. */
const ItemActionsRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;

    ${breakpoint.breakOnlyMobile`
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
    `};
`;

/* Grey rather than branded: leaving the step is the lesser of the two actions,
   and Save keeps the accent border. */
const BackToCartButton = styled(BorderButton)`
    border-color: ${(p) => p.theme.colors.gray_5};
    color: ${(p) => p.theme.colors.gray_1};
    font-weight: 600;

    &:hover:not(:disabled) {
        background: ${(p) => p.theme.colors.gray_6};
    }
`;

interface ICheckoutStep2Props {
    items: ICartItemData[];
    customers: ICustomerDetail[];
    moveTo: (step: number) => void;
    onItemsSave: (items: ICartItemData[], shouldGoToNextStep: boolean) => Promise<void>;
    onCreateNewCustomer: (customer: ISetCustomerData) => Promise<any>;
    handleOrderCreate?: (
        paymentType: string,
        pointsApplied: number,
        commissionApplied?: number | null,
    ) => Promise<void>;
    onOrderCreateSuccess?: () => void;
    quoteId?: string;
    isQuotesPage?: boolean;
}
interface FieldStatus {
    hasCommonRedeemers: boolean;
    commonNames: string[];
    sharedOptionalData: Record<string, string>;
    fieldIdMap: Record<string, string>; // Add this line
}

export const CheckoutStep2 = ({
    items,
    customers,
    moveTo,
    onItemsSave,
    onCreateNewCustomer,
    handleOrderCreate,
    onOrderCreateSuccess,
    quoteId,
    isQuotesPage,
}: ICheckoutStep2Props) => {
    const [cartItems, setCartItems] = useState<ICartItemData[]>(() => items);

    useEffect(() => {
        setCartItems(items);
        loadedCheckInItemIds.current.clear();
        setCheckInOptionsMap({});
    }, [items]);

    const itemsByProduct = useMemo(() => groupCartItemsByProduct(cartItems), [cartItems]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSavingToQuote, setIsSavingToQuote] = useState(false);
    const [showQuoteDropdown, setShowQuoteDropdown] = useState(false);
    const [isLoadingBookingDetails, setIsLoadingBookingDetails] = useState(false);
    const [checkInOptionsMap, setCheckInOptionsMap] = useState<{
        [key: string]: IBookingCheckInOptions;
    }>({});
    const loadedCheckInItemIds = useRef<Set<number>>(new Set());
    /**
     * A plain ref, deliberately not useDynamicBottomHeight.
     *
     * That hook publishes this container's height as the global
     * `--bottom-container-height`, which CheckoutStep1 and CheckoutStep3 use to
     * leave room for a fixed bottom bar. This container is no longer fixed, so
     * publishing its full height would push an empty gap of that size into both.
     *
     * The auto-scroll that used to sit here went with it: it dragged the capped
     * sheet to its own bottom so Proceed to Payment was reachable, and there is
     * no longer a cap or an inner scrollbar to drag.
     */
    const totalPriceContainerRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { account, metadata } = useAppSelector(selectAccount);
    const sharedByEmail = useSharedByEmail();
    const isBookNow = window.location.pathname.includes(PATH_BOOK_NOW);
    const isLoggedIn = useIsLoggedIn();
    const isAgent = !!(metadata?.is_commission_agent || metadata?.is_points_agent);

    // For authenticated users, customers already contains all user redeemers (via user endpoint)
    // For guests, customers contains session-based redeemers
    const suggestionRedeemers = customers;

    const isPointsUpgraded = account?.is_agent_integrated;

    const handleSaveToQuote = async () => {
        if (!isLoggedIn) {
            ErrorToast({ Message: "Please login first to create quotes" });
            return;
        }
        if (cartItems.length === 0) {
            ErrorToast({ Message: "No items to save" });
            return;
        }
        try {
            setIsSavingToQuote(true);
            const addItemBases = transformCartItemsForQuote(cartItems);
            const firstItem = addItemBases[0];
            const title = await createNewQuoteModal();
            if (!title) {
                setIsSavingToQuote(false);
                return;
            }
            await dispatch(createNewQuoteAndReload({ ...firstItem, quoteTitle: title })).unwrap();
            if (addItemBases.length > 1) {
                await dispatch(loadQuotes());
                const allQuotes = (await import("@redux")).store.getState().allQuotes;
                const createdQuote = (
                    allQuotes.allQuotes?.length > 0 ? allQuotes.allQuotes : allQuotes.quotes
                ).find((q: { title: string }) => q.title.toLowerCase() === title.toLowerCase());
                if (createdQuote) {
                    const remainingItems = addItemBases.slice(1);
                    await Promise.allSettled(
                        remainingItems.map((itemBase) =>
                            dispatch(
                                addItemToExistingQuoteAndReload({
                                    ...itemBase,
                                    quoteId: String(createdQuote.id),
                                }),
                            ).unwrap(),
                        ),
                    );
                }
            }
            navigate(PATH_QUOTES);
        } catch (error: any) {
            console.error("Error saving to quote:", error);
            ErrorToast({ Message: error?.title || error?.message || "Failed to save to quote" });
        } finally {
            setIsSavingToQuote(false);
        }
    };

    const handleAddToExistingQuote = async () => {
        if (!isLoggedIn) {
            ErrorToast({ Message: "Please login first to create quotes" });
            return;
        }
        if (cartItems.length === 0) {
            ErrorToast({ Message: "No items to save" });
            return;
        }
        try {
            setIsSavingToQuote(true);
            await dispatch(loadQuotes());
            const selectedQuote = await addToExistingQuoteModal();
            if (!selectedQuote) {
                setIsSavingToQuote(false);
                return;
            }
            const addItemBases = transformCartItemsForQuote(cartItems);
            await Promise.allSettled(
                addItemBases.map((itemBase) =>
                    dispatch(
                        addItemToExistingQuoteAndReload({
                            ...itemBase,
                            quoteId: String(selectedQuote.id),
                        }),
                    ).unwrap(),
                ),
            );
            navigate(PATH_QUOTES);
        } catch (error: any) {
            console.error("Error adding to existing quote:", error);
            ErrorToast({ Message: error?.title || error?.message || "Failed to add to quote" });
        } finally {
            setIsSavingToQuote(false);
        }
    };

    const getOptionalFieldStatus = (cartItems: ICartItemData[]): FieldStatus => {
        // Track all unique field common names across all items
        const allNames = new Set<string>();
        // Count occurrences of each common name
        const nameCounts = new Map<string, number>();
        // Map common names to their field IDs
        const commonNameToFieldId = new Map<string, string>();
        // Store first encountered values for each common name
        const commonNameValues = new Map<string, string>();

        // First pass: Collect all common names from optional fields
        cartItems.forEach((item) => {
            // Get optional fields definition for this item
            const itemFields = item.booking_details?.optionalFields || [];

            // Extract and clean common names
            const itemNames = itemFields
                .filter((f) => f.commonName?.trim())
                .map((f) => f.commonName!.trim());

            // Update global name tracking
            itemNames.forEach((name) => {
                allNames.add(name);
                nameCounts.set(name, (nameCounts.get(name) || 0) + 1);
            });
        });

        // Second pass: Map field IDs and collect initial values
        cartItems.forEach((item) => {
            // Create commonName -> fieldID mapping
            item.booking_details?.optionalFields?.forEach((field) => {
                const cleanName = field.commonName?.trim();
                if (cleanName && field.optionalFieldId) {
                    commonNameToFieldId.set(cleanName, field.optionalFieldId.toString());
                }
            });

            // Collect initial values from booking data
            item.booking_data?.forEach((bd) => {
                allNames.forEach((name) => {
                    const fieldId = commonNameToFieldId.get(name);
                    if (!fieldId) return;

                    // Get value from either field ID or common name
                    const value = bd.optionalData?.[fieldId] || bd.optionalData?.[name];
                    if (value) {
                        // Store first encountered value
                        if (!commonNameValues.has(name)) {
                            commonNameValues.set(name, value);
                        }
                    }
                });
            });
        });

        // Final field status aggregation
        const result: FieldStatus = {
            hasCommonRedeemers: allNames.size > 0, // Any shared fields?
            commonNames: Array.from(allNames), // List of all unique names
            sharedOptionalData: _.fromPairs(Array.from(commonNameValues.entries())), // First values
            fieldIdMap: _.fromPairs(Array.from(commonNameToFieldId.entries())), // Name->ID mapping
        };
        return result;
    };

    const fillOptionalDataByMatchingCommonNames = (cartItems: ICartItemData[]) => {
        // Create working copy of items to modify
        const modifiedItems = _.cloneDeep(cartItems);
        // Get field metadata and mappings
        const fieldStatus = getOptionalFieldStatus(cartItems);

        // Flatten all booking data entries with their parent items
        const allBookingData = _.flatMap(modifiedItems, (item) =>
            item.booking_data.map((bd) => ({
                item,
                bd,
                // Create consistent redeemer ID ordering for grouping

                sortedRedeemers: JSON.stringify(_.sortBy(bd.redeemers || [])),
                productId: item.tdms_product_id,
            })),
        );

        // Group 1: By sorted redeemer IDs
        const groupedByRedeemers = _.groupBy(allBookingData, "sortedRedeemers");

        // Phase 1: Propagate common field values
        _.forEach(groupedByRedeemers, (bdGroup) => {
            fieldStatus.commonNames.forEach((commonName) => {
                const fieldId = fieldStatus.fieldIdMap[commonName];
                if (!fieldId) return;

                // gather any existing values in the group
                const values = bdGroup
                    .map(({ bd }) => bd.optionalData?.[fieldId] ?? bd.optionalData?.[commonName])
                    .filter((v) => v !== undefined && v !== null);

                if (values.length === 0) return;
                const mostRecentValue = values[values.length - 1];

                bdGroup.forEach(({ bd }) => {
                    // OPTION A: explicit undefined checks
                    const hasFieldIdProp = bd.optionalData?.[fieldId] !== undefined;
                    const hasCommonNameProp = bd.optionalData?.[commonName] !== undefined;
                    const shouldPropagate = !hasFieldIdProp && !hasCommonNameProp;

                    // OPTION B: hasOwnProperty check
                    // const hasFieldIdProp = Object.prototype.hasOwnProperty.call(bd.optionalData ?? {}, fieldId);
                    // const hasCommonNameProp = Object.prototype.hasOwnProperty.call(bd.optionalData ?? {}, commonName);
                    // const shouldPropagate = !hasFieldIdProp && !hasCommonNameProp;

                    if (shouldPropagate) {
                        bd.optionalData = {
                            ...bd.optionalData,
                            [fieldId]: mostRecentValue,
                        };
                    }
                });
            });
        });

        // Phase 2: Propagate pickup locations within same redeemer group
        // Only fills empty pickup locations when same travelers are used across items
        _.forEach(groupedByRedeemers, (bdGroup) => {
            const validPickups = bdGroup.filter((b) => b.bd.pickupId && b.bd.pickupLocation);
            if (validPickups.length > 0) {
                const sourcePickup = validPickups[0].bd;
                bdGroup.forEach(({ bd }) => {
                    if (!bd.pickupId || !bd.pickupLocation) {
                        bd.pickupId = sourcePickup.pickupId;
                        bd.pickupLocation = sourcePickup.pickupLocation;
                    }
                });
            }
        });

        return modifiedItems;
    };
    useEffect(() => {
        const loadAllBookingDetails = async () => {
            const promises: Promise<void>[] = [];

            itemsByProduct.forEach((itemByProduct) => {
                itemByProduct.itemsByFare.forEach((itemByFare) => {
                    itemByFare.itemsByGroup.forEach((itemByGroup) => {
                        const checkInDateItem = getCheckInDateItem(itemByGroup.items);
                        if (
                            checkInDateItem &&
                            !loadedCheckInItemIds.current.has(checkInDateItem.id)
                        ) {
                            loadedCheckInItemIds.current.add(checkInDateItem.id);
                            promises.push(
                                getBookingCheckInOptions(checkInDateItem)
                                    .then((checkInOptions) => {
                                        if (checkInOptions) {
                                            setCheckInOptionsMap((prev) => ({
                                                ...prev,
                                                [checkInDateItem.id]: checkInOptions,
                                            }));
                                        }
                                    })
                                    .catch(() => {
                                        loadedCheckInItemIds.current.delete(checkInDateItem.id);
                                    }),
                            );
                        }
                    });
                });
            });

            if (promises.length > 0) {
                setIsLoadingBookingDetails(true);
                try {
                    await Promise.allSettled(promises);
                } finally {
                    setIsLoadingBookingDetails(false);
                }
            }
        };

        loadAllBookingDetails();
    }, [itemsByProduct]);

    const handleGroupItemsChange = (updatedItems: ICartItemData[]) => {
        setCartItems((prevItems) => {
            const mergedItems = prevItems.map(
                (cartItem) => updatedItems.find((item) => item.id === cartItem.id) || cartItem,
            );
            return prevItems === mergedItems
                ? mergedItems
                : fillOptionalDataByMatchingCommonNames(mergedItems);
        });
    };

    const handleSaveItems = async (shouldGoToNextStep: boolean) => {
        try {
            setIsSubmitting(true);

            if (shouldGoToNextStep) {
                if (sharedByEmail && !isPointsUpgraded) {
                    await onItemsSave(cartItems, false);
                    navigate(
                        `${PATH_LINK_AGENT_ACCOUNT}?referral=${encodeURIComponent(sharedByEmail)}`,
                        { state: { from: location } },
                    );
                    return;
                }

                const validation = validateCheckoutStep(
                    itemsByProduct,
                    _.values(checkInOptionsMap),
                );
                if (!validation.isValid) {
                    ErrorToast({ Message: validation.message! });
                    return;
                }
            }

            await onItemsSave(cartItems, shouldGoToNextStep);
        } catch (error) {
            console.error("Error saving items:", error);
            ErrorToast({ Message: "Please complete all required fields before proceeding" });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleValidateAndCreateOrder = async (
        paymentType: string,
        pointsApplied: number,
        commissionApplied?: number | null,
    ) => {
        if (sharedByEmail && !isPointsUpgraded) {
            await onItemsSave(cartItems, false);
            navigate(`${PATH_LINK_AGENT_ACCOUNT}?referral=${encodeURIComponent(sharedByEmail)}`, {
                state: { from: location },
            });
            return;
        }

        const validation = validateCheckoutStep(itemsByProduct, _.values(checkInOptionsMap));
        if (!validation.isValid) {
            ErrorToast({ Message: validation.message! });
            focusInvalidTravellerField(validation.invalidItemId, validation.invalidQuantityIndex);
            return;
        }

        try {
            await onItemsSave(cartItems, false);
        } catch (error) {
            console.error("Error saving items before order:", error);
            ErrorToast({ Message: "Please complete all required fields before proceeding" });
            return;
        }

        if (handleOrderCreate) {
            await handleOrderCreate(paymentType, pointsApplied, commissionApplied);
        }
    };

    return (
        <Container>
            <div className="cartWrapper">
                {sharedByEmail && (
                    <SharedByBanner>
                        Shared by: <span>{sharedByEmail}</span>
                    </SharedByBanner>
                )}
                {itemsByProduct.map((itemByProduct, productIndex) => {
                    return (
                        <CartItemsByProduct
                            key={productIndex}
                            productId={itemByProduct.productId}
                            name={itemByProduct.name}
                            productClass={itemByProduct.productClass}
                            productImagePath={itemByProduct.productImagePath}
                            itemsByFare={itemByProduct.itemsByFare}
                            startLocationName={itemByProduct.startLocationName}
                            supplierName={itemByProduct.supplierName || ""}
                            onItemsChange={handleGroupItemsChange}
                            onCreateNewCustomer={onCreateNewCustomer}
                            customers={customers}
                            suggestionRedeemers={suggestionRedeemers}
                            checkInOptionsMap={checkInOptionsMap}
                            isLoadingBookingDetails={isLoadingBookingDetails}
                            onValidateBeforeSubmit={() => {
                                const validation = validateCheckoutStep(
                                    itemsByProduct,
                                    _.values(checkInOptionsMap),
                                );
                                if (!validation.isValid) {
                                    ErrorToast({ Message: validation.message! });
                                    return false;
                                }
                                return true;
                            }}
                        />
                    );
                })}
                {!isBookNow && (
                    <ItemActionsRow>
                        <BorderButton
                            $borderRadius={theme.borderRadius.small}
                            color="transparent"
                            $textColor={
                                isSubmitting ? theme.colors.gray_6 : theme.colors.button_text1
                            }
                            type="submit"
                            disabled={isSubmitting}
                            onClick={() => handleSaveItems(false)}
                        >
                            {isSubmitting ? "Saving ..." : "Save"}
                        </BorderButton>
                        <BackToCartButton
                            $borderRadius={theme.borderRadius.small}
                            color="transparent"
                            $textColor={theme.colors.gray_1}
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => moveTo(0)}
                        >
                            Go back to cart
                        </BackToCartButton>
                    </ItemActionsRow>
                )}
            </div>
            <div className="totalPriceContainer" ref={totalPriceContainerRef}>
                {handleOrderCreate ? (
                    <>
                        <PaymentOptionComponent
                            handleOrderCreate={handleValidateAndCreateOrder}
                            customers={customers}
                            onOrderCreateSuccess={onOrderCreateSuccess}
                            items={cartItems}
                            quoteId={quoteId}
                            isQuotesPage={isQuotesPage}
                            embedded
                        />
                        {isLoggedIn && isAgent && !isQuotesPage && (
                            <SaveToQuoteInlineDropdown>
                                <div
                                    className={`button-row ${isSubmitting || isSavingToQuote ? "disabled" : ""}`}
                                >
                                    <div
                                        className="main-label"
                                        onClick={() => {
                                            if (!isSubmitting && !isSavingToQuote)
                                                handleSaveToQuote();
                                        }}
                                    >
                                        {isSavingToQuote ? "Saving..." : "Save as new quote"}
                                    </div>
                                    <div className="divider" />
                                    <button
                                        className="chevron-toggle"
                                        onClick={() => {
                                            if (!isSubmitting && !isSavingToQuote)
                                                setShowQuoteDropdown((prev) => !prev);
                                        }}
                                        disabled={isSubmitting || isSavingToQuote}
                                    >
                                        <img
                                            src="/images/chevron-up.svg"
                                            alt="chevron"
                                            className={`chevron ${showQuoteDropdown ? "rotated" : ""}`}
                                        />
                                    </button>
                                </div>
                                {showQuoteDropdown && !isSubmitting && !isSavingToQuote && (
                                    <div className="dropdown-menu">
                                        <div
                                            className="dropdown-item"
                                            onClick={() => {
                                                setShowQuoteDropdown(false);
                                                handleAddToExistingQuote();
                                            }}
                                        >
                                            Add to existing quote
                                        </div>
                                    </div>
                                )}
                            </SaveToQuoteInlineDropdown>
                        )}
                    </>
                ) : (
                    <CheckoutItemsActions
                        btnTitle={sharedByEmail && !isPointsUpgraded ? "Upgrade & Pay" : "Payment"}
                        isSubmitting={isSubmitting || isLoadingBookingDetails}
                        onBtnClick={() => handleSaveItems(true)}
                        page="Checkout"
                        onSaveToQuote={
                            isLoggedIn && isAgent && !isQuotesPage ? handleSaveToQuote : undefined
                        }
                        onAddToExistingQuote={
                            isLoggedIn && isAgent && !isQuotesPage
                                ? handleAddToExistingQuote
                                : undefined
                        }
                        isSavingToQuote={isSavingToQuote}
                    />
                )}
            </div>
        </Container>
    );
};

const SaveToQuoteInlineDropdown = styled.div`
    position: relative;
    width: 100%;
    margin-top: 8px;

    .button-row {
        display: flex;
        align-items: center;
        height: 44px;
        border: 1px solid ${theme.colors.main};
        border-radius: ${theme.borderRadius.small};
        overflow: hidden;
        cursor: pointer;

        &.disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    .main-label {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: "Poppins", sans-serif;
        font-size: 14px;
        font-weight: 600;
        color: ${theme.colors.button_text1};
        height: 100%;
        padding: 0 12px;
    }

    .divider {
        width: 1px;
        height: 60%;
        background: ${theme.colors.main};
    }

    .chevron-toggle {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 100%;
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 0;

        .chevron {
            width: 16px;
            height: 16px;
            transition: transform 0.2s;
        }

        .chevron.rotated {
            transform: rotate(180deg);
        }
    }

    .dropdown-menu {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: #fff;
        border: 1px solid #e6e9eb;
        border-radius: 6px;
        margin-top: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        z-index: 10;
    }

    .dropdown-item {
        padding: 10px 14px;
        font-family: "Poppins", sans-serif;
        font-size: 13px;
        color: #131214;
        cursor: pointer;
        transition: background 0.15s;

        &:hover {
            background: #f4f6f7;
        }
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    font-family: Poppins;
    gap: 20px;
    margin: 0 auto;

    ${breakpoint.breakTablet`
        width: ${(p) => p.theme.widths.wide_700};
    `}

    ${breakpoint.breakIpadPro`
        width: ${(p) => p.theme.widths.wide_900};
    `}

    ${breakpoint.breakLarge`
        width: ${(p) => p.theme.widths.wide_1110};
    `}
    
    .cartWrapper {
        position: relative;
        width: 100%;
        min-width: 0;
    }

    ${breakpoint.breakOnlyMobile`
        padding: 16px;
    `}

    /* Flows with the page on a phone, as it does on desktop. It used to be fixed
       to the bottom at half the viewport height with its own scrollbar, which
       split the checkout into two independently scrolling halves — travellers
       above, points and summary below — and gave each about half a screen to
       work in. */
    .totalPriceContainer {
        width: 100%;
        ${breakpoint.breakOnlyMobile`
            margin-top: 24px;
        `}
    }
`;

const SharedByBanner = styled.div`
    position: absolute;
    top: -28px;
    left: 0;
    color: ${theme.colors.gray_3};
    font-size: 14px;
    line-height: 21px;

    ${breakpoint.breakOnlyMobile`
        width: 225px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    `}

    span {
        color: ${theme.colors.black};
    }
`;

const PlainSaveButton = styled.button`
    border: none;
    background: none;
    padding: 8px 4px;
    font-size: 14px;
    color: ${theme.colors.gray_3};
    text-decoration: underline;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    @media (hover: hover) {
        &:hover:not(:disabled) {
            color: ${theme.colors.gray_1};
        }
    }
`;
